import { useRef, useEffect } from 'react';
import { ScrollArea } from '../ui/scroll-area';
import { MessageSquare, Loader2 } from 'lucide-react';
import { format } from 'date-fns';

const formatMessageTime = (timestamp) => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  const now = new Date();
  const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return format(date, 'HH:mm');
  } else if (diffDays === 1) {
    return 'Yesterday ' + format(date, 'HH:mm');
  } else if (diffDays < 7) {
    return format(date, 'EEE HH:mm');
  }
  return format(date, 'dd/MM HH:mm');
};

const ChatMessageList = ({ messages, loading, userId }) => {
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  if (loading) {
    return (
      <ScrollArea className="flex-1 p-4">
        <div className="flex items-center justify-center h-32">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </ScrollArea>
    );
  }

  if (messages.length === 0) {
    return (
      <ScrollArea className="flex-1 p-4">
        <div className="text-center text-muted-foreground py-8">
          <MessageSquare className="h-12 w-12 mx-auto mb-3 opacity-50" />
          <p>No messages yet</p>
          <p className="text-sm">Start the conversation!</p>
        </div>
      </ScrollArea>
    );
  }

  return (
    <ScrollArea className="flex-1 p-4">
      <div className="space-y-3">
        {messages.map((msg, idx) => {
          const isOwn = msg.sender_id === userId;
          const isSystem = msg.type === 'system';

          if (isSystem) {
            return (
              <div key={msg.id || idx} className="text-center text-xs text-muted-foreground py-1">
                {msg.content}
              </div>
            );
          }

          return (
            <div
              key={msg.id || idx}
              className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[80%] rounded-lg px-3 py-2 ${
                  isOwn
                    ? 'bg-[#ffd108] text-black'
                    : 'bg-muted'
                }`}
              >
                {!isOwn && (
                  <p className="text-xs font-medium mb-1 opacity-70">
                    {msg.sender_name}
                  </p>
                )}
                <p className="text-sm whitespace-pre-wrap break-words">
                  {msg.content}
                </p>
                <p className={`text-[10px] mt-1 ${isOwn ? 'text-black/60' : 'text-muted-foreground'}`}>
                  {formatMessageTime(msg.created_at)}
                </p>
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>
    </ScrollArea>
  );
};

export default ChatMessageList;
